import { FormInputProps } from '@/@types/form.types';
import { cn } from '@/lib/utils';
import { Controller } from 'react-hook-form';
import InformationCircle from '@/components/icons/InformationCircle';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuCheckboxItem,
    DropdownMenuTrigger,
    DropdownMenuCheckboxItem2,
} from '@/components/ui/dropdown-menu';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import { useTranslation } from 'react-i18next';

type Props<TForm extends Record<string, any>, TField> = {
    label?: string;
    list: { name: string; value: TField | null }[];
    optional?: boolean;
    selectVariant?: 'single' | 'multi';
    variant?: 'type1' | 'type2';
} & FormInputProps<TForm>;

const EPopupPicker = <TForm extends Record<string, any>, TField>({
    label,
    placeholder = 'Select from list',
    list,
    optional = false,
    fieldName,
    control,
    containerClassName,
    disabled,
    selectVariant = 'single',
    variant = 'type1',
}: Props<TForm, TField>) => {
    const [isOpen, setIsOpen] = useState(false);

    const { t } = useTranslation();

    const isOpenToggler = () => {
        setIsOpen((prev) => !prev);
    };

    const variants = {
        type1: DropdownMenuCheckboxItem,
        type2: DropdownMenuCheckboxItem2,
    };

    const ItemComponent = variants[variant];

    return (
        <div className={cn('w-full flex flex-col gap-1.5 select-none', containerClassName)}>
            {label && (
                <h4 className="text-sm font-medium">
                    {label}
                    {optional && <span className="text-popover-600"> (optional)</span>}
                </h4>
            )}
            <Controller
                name={fieldName}
                control={control}
                render={({ field: { value, onChange, onBlur }, fieldState, formState }) => {
                    const isMulti = selectVariant === 'multi';

                    const selectedValues = Array.isArray(value)
                        ? (value as (TField | null)[])
                        : value !== null && value !== undefined
                        ? [value]
                        : [];

                    const toggleValue = (tag: TField | null) => {
                        if (isMulti) {
                            if (selectedValues.includes(tag as TField)) {
                                onChange(selectedValues.filter((v) => v !== tag));
                            } else {
                                onChange([...selectedValues, tag as TField]);
                            }
                        } else {
                            onChange(tag);
                            isOpenToggler();
                        }
                    };

                    const selectedLabels = selectedValues
                        .map((val) => {
                            const item = list.find((el) => el.value === val);
                            return item ? t(item.name) : '';
                        })
                        .filter(Boolean)
                        .join(', ');

                    return (
                        <DropdownMenu open={isOpen} onOpenChange={isOpenToggler} modal={true}>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" disabled={disabled || formState.disabled}>
                                    <span className="text-xs text-popover-1000 truncate">
                                        {selectedLabels || placeholder}
                                    </span>
                                    <div
                                        className={cn(
                                            'transition-transform duration-50 ease-in-out ml-auto',
                                            isOpen ? '-rotate-180' : 'rotate-0',
                                        )}
                                    >
                                        <ArrowDownIcon />
                                    </div>
                                </Button>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width) max-h-50 overflow-y-scroll no-scrollbar rounded-semi-xl border border-popover-100 bg-popover-10">
                                {list.map(({ name, value: val }, index) => (
                                    <ItemComponent
                                        key={index}
                                        className="px-3.5 py-3"
                                        checked={selectedValues.includes(val)}
                                        onSelect={(e) => e.preventDefault()}
                                        onCheckedChange={() => toggleValue(val)}
                                        onBlur={onBlur}
                                    >
                                        {t(name)}
                                    </ItemComponent>
                                ))}
                            </DropdownMenuContent>

                            {fieldState.invalid && (
                                <div className="flex items-center gap-1.5">
                                    <InformationCircle className="size-4 stroke-error-1000" />
                                    <span className="text-xs text-error-1000">
                                        {t(`${fieldState.error?.message}`)}
                                    </span>
                                </div>
                            )}
                        </DropdownMenu>
                    );
                }}
            />
        </div>
    );
};

export default EPopupPicker;
